import { AlertTriangle, CircleAlert, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FaqSeverity, FaqVerdict } from '../../types/faq-monitor.js';

/**
 * 重大度バッジの見た目。
 *
 * 透過（`/10`）で面を作り、文字色だけをモードで切り替える。ライト側で
 * `bg-red-100` のような不透明な淡色を使うと地の白から浮いて読みづらい。
 * data-integrity の `IntegrityBadges` と同じ流儀に揃えている。
 * @graph-connects none
 */
const SEVERITY_CLS: Record<FaqSeverity, string> = {
  高: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  中: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  低: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
};

/** @graph-connects none */
const SEVERITY_ICON: Record<FaqSeverity, typeof ShieldAlert> = {
  高: ShieldAlert,
  中: AlertTriangle,
  低: CircleAlert,
};

/** @graph-connects none */
const UNKNOWN_SEVERITY_CLS = 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20';

/**
 * 重大度の並び順。数字が小さいほど先に出す。重大度なしは最後に回す。
 * @graph-connects none
 */
const SEVERITY_ORDER: Record<FaqSeverity, number> = { 高: 0, 中: 1, 低: 2 };

/**
 * 絞り込みに出す重大度。`すべて` は null で表す。
 * @graph-connects none
 */
export const SEVERITY_FILTERS: readonly FaqSeverity[] = ['高', '中', '低'];

/**
 * 絞り込みに出す判定。指摘として画面に出るのはこの2つだけ。
 * @graph-connects none
 */
export const VERDICT_FILTERS: readonly FaqVerdict[] = ['矛盾', '不足'];

/**
 * 並べ替え用の重み。重大度なしは既知のどれよりも後ろに置く。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 重大度を並べ替え可能な数値にし、重大度なしを末尾へ回す
 * @graph-connects none
 */
export function severityWeight(severity: FaqSeverity | null): number {
  return severity === null ? SEVERITY_FILTERS.length : SEVERITY_ORDER[severity];
}

/**
 * 重大度に対応する見た目を返す。重大度なしは灰色に倒す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 重大度ごとの配色を1箇所に集約して、バッジと判定チップで同じ色を使う
 * @graph-connects none
 */
export function severityClass(severity: FaqSeverity | null): string {
  return severity === null ? UNKNOWN_SEVERITY_CLS : SEVERITY_CLS[severity];
}

/**
 * 重大度バッジ。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 指摘の重大度をライト・ダーク両対応のバッジで表示する
 * @graph-connects severityClass [calls] 重大度ごとの配色
 */
export function SeverityBadge({ severity }: { severity: FaqSeverity | null }) {
  const { t } = useTranslation();
  const Icon = severity === null ? CircleAlert : SEVERITY_ICON[severity];

  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${severityClass(severity)}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {severity ?? t('faqMonitor.severityUnknown')}
    </span>
  );
}

/**
 * 判定チップ（矛盾 / 不足）。
 *
 * 判定は「何が起きているか」の分類なので、重大度と同じ色を載せて行頭に置く。
 * 右端の小さい灰色文字だと、いちばん重要な分類がいちばん目立たなくなる。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 指摘の種別を重大度の色つきチップとして行頭に表示する
 * @graph-connects severityClass [calls] 重大度ごとの配色
 */
export function VerdictChip({
  verdict,
  severity,
}: {
  verdict: FaqVerdict;
  severity: FaqSeverity | null;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded border px-2 py-0.5 text-xs font-medium ${severityClass(severity)}`}
    >
      {verdict}
    </span>
  );
}

/**
 * 重大度ごとの件数チップ。件数0の重大度は出さない。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 記事が抱える指摘を重大度ごとの件数チップで示す
 * @graph-connects severityClass [calls] 重大度ごとの配色
 */
export function SeverityCountChip({ severity, count }: { severity: FaqSeverity; count: number }) {
  return (
    <span
      className={`shrink-0 rounded border px-2 py-0.5 text-xs font-medium tabular-nums ${severityClass(severity)}`}
    >
      {severity} {count}
    </span>
  );
}
